import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountingService } from '../../../../services/accounting.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-transaction-detail',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, CardModule, TagModule],
  template: `
    <div class="p-4">
      <div class="flex align-items-center gap-3 mb-4">
        <p-button icon="pi pi-arrow-left" [text]="true" (onClick)="router.navigate(['/dashboard'])"></p-button>
        <h1 class="text-3xl font-bold m-0">{{ title }}</h1>
      </div>

      <div class="grid mb-4">
        <div class="col-12 md:col-4">
          <p-card header="Total Amount" subheader="For this selection">
             <h2 class="text-3xl font-bold" [class.text-green-500]="type === 'income'" [class.text-red-500]="type === 'expense'">
               {{ totalAmount | currency }}
             </h2>
          </p-card>
        </div>
        <div class="col-12 md:col-4">
          <p-card header="Count" subheader="Transactions">
             <h2 class="text-3xl font-bold">{{ filteredTransactions.length }}</h2>
          </p-card>
        </div>
      </div>

      <p-card [header]="'Transaction Breakdown - ' + monthLabel">
        <p-table [value]="filteredTransactions" [rows]="10" [paginator]="true" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Source</th>
              <th>Destination</th>
              <th class="text-right">Amount</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-tx>
            <tr>
              <td>{{ tx.date | date }}</td>
              <td>{{ tx.description }}</td>
              <td>{{ getAccountName(tx.from_account_id) }}</td>
              <td>{{ getAccountName(tx.to_account_id) }}</td>
              <td class="text-right font-bold">{{ tx.amount | currency }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </div>
  `
})
export class TransactionDetailComponent implements OnInit {
  monthIndex: number = -1;
  year: number = 0;
  type: string = 'income'; // 'income' or 'expense'
  accountId: string = '';

  transactions: any[] = [];
  accounts: any[] = [];
  filteredTransactions: any[] = [];

  title: string = '';
  monthLabel: string = '';
  totalAmount: number = 0;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private accountingService: AccountingService
  ) { }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.monthIndex = params['month'] !== undefined ? +params['month'] : -1;
      this.year = params['year'] !== undefined ? +params['year'] : 0;
      this.type = params['type'] || 'income';
      this.accountId = params['accountId'] || '';

      this.loadData();
    });
  }

  loadData() {
    this.accountingService.getAccounts().subscribe(accs => {
      this.accounts = accs;
      this.accountingService.getTransactions().subscribe(txs => {
        this.transactions = txs;
        this.filterTransactions();
      });
    });
  }

  filterTransactions() {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.monthLabel = this.monthIndex >= 0 ? monthNames[this.monthIndex] : 'All Time';

    let displayType = this.type === 'income' ? 'Income' : 'Expense';
    if (this.accountId) {
      const acc = this.accounts.find(a => a.id === this.accountId);
      if (acc) displayType = acc.name;
    }

    this.title = `${displayType} Details - ${this.monthLabel} ${this.year || ''}`;

    this.filteredTransactions = this.transactions.filter(tx => {
      if (this.year > 0) {
        const d = new Date(tx.date);
        const mMatch = this.monthIndex >= 0 ? d.getMonth() === this.monthIndex : true;
        const yMatch = d.getFullYear() === this.year;
        if (!mMatch || !yMatch) return false;
      }

      if (this.accountId) {
        return tx.from_account_id === this.accountId || tx.to_account_id === this.accountId;
      }

      const fromAcc = this.accounts.find(a => a.id === tx.from_account_id);
      const toAcc = this.accounts.find(a => a.id === tx.to_account_id);

      if (this.type === 'income') {
        return fromAcc?.type === 'revenue';
      } else {
        return toAcc?.type === 'expense';
      }
    });

    this.totalAmount = this.filteredTransactions.reduce((sum, tx) => sum + tx.amount, 0);
  }

  getAccountName(id: string): string {
    const acc = this.accounts.find(a => a.id === id);
    return acc ? acc.name : id;
  }
}
